// @ts-strict-ignore
import { LoadProjectInfoService } from '@project/service/load-project-info.service';
import { UndoRedoManager } from './undo-redo.manager';
import { IOperation } from './operation/i-operation';
import { LabelStripArticleManager } from '@3d-scene/manager/label-strip-article.manager';
import { IMoveArticlesOperation } from './operation/i-move-articles-operation';
import { UndoRedoUtils } from './undo-redo-utils';
import { ProjectModel } from '@project/model/project.model';
import { forkJoin, lastValueFrom, Observable, of } from 'rxjs';
import { ReplaceLabelingArticleHelper } from '@project/components/label/replace-labeling-helper/replace-labeling-article.helper';
import { filter, map } from 'rxjs/operators';
import { DirectPrintingHelper } from '@article/helper/direct-printing/direct-printing.helper';
import { UndoRotateArticlesOperation } from './operation/undo-rotate-articles-operation';
import { ArrayUtils } from '../util/array-utils';
import { Injectable } from '@angular/core';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { UndoDetachArticlesOperation } from './operation/undo-detach-articles-operation';
import { UndoAttachArticlesOperation } from './operation/undo-attach-articles-operation';
import { Log } from '@shared/debug/log';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { InsertedArticleData } from '@project/components/article/processor/i-article-location.processor';
import { UndoInsertArticlesByLocationOperation } from './operation/undo-insert-articles-by-location-operation';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';

@Injectable({ providedIn: 'root' })
export class UndoRedoHelper {
  constructor(
    private undoRedoManager: UndoRedoManager,
    private projectModel: ProjectModel,
    private selectionModelService: SelectionModelService,
    private labelStripArticleManager: LabelStripArticleManager,
    private replaceLabelingHelper: ReplaceLabelingArticleHelper,
    private directPrintingHelper: DirectPrintingHelper,
    private undoRedoUtils: UndoRedoUtils,
    private insertArticleService: InsertArticleService,
    private insertArticleUtils: InsertArticleUtils,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService,
    private replaceBusbarHelper: ReplaceBusbarArticleHelper,
    private loadProjectInfoService: LoadProjectInfoService,
  ) {
    this.subscribeOnArticleInserted();
  }

  private subscribeOnArticleInserted(): void {
    this.projectModel.articleInsertedSubscription = this.insertArticleService.articleInserted$
    .pipe(filter(data=> data.messageType === 'insertMountingRailComponents'))
    .subscribe({
      next: (data) => {
        if (!data.error) {
          this.mountingRailComponentsInserted(data.insertedArticleData)
        }
      },
      error: (error) => Log.error('Error in article inserted Subscription: ', error)
    })
  }

  private async mountingRailComponentsInserted(data: InsertedArticleData): Promise<void> {
    await this.pushUndoOperation(new UndoInsertArticlesByLocationOperation(
      this.createUndoAttachOperationByUsageIds([data.baseArticleGUID]),
      data.moveArticlesMap,
      this.insertArticleUtils
    )
    );
  }

  public async pushUndoOperation(undoOperation: IOperation): Promise<void> {
    if(this.loadProjectInfoService.isLoading) return;
    const operationObservable = await this.pushUndoOperationObservable(undoOperation);
    await lastValueFrom(operationObservable);
  }

  public async pushUndoOperationObservable(undoOperation: IOperation): Promise<Observable<void>> {
    const undoObservable = await this.buildAdditionalUndoActions(undoOperation);
    return undoObservable.pipe(
      map((operationsToPush: IOperation[]) => this.addAdditionalUndoActions(operationsToPush, this.getAdditionalOperations(undoOperation)))
    );
  }

  private async buildAdditionalUndoActions(undoOperation: IOperation): Promise<Observable<IOperation[]>> {
    const undoActions: Observable<IOperation>[] = [];
    // pre-actions
    undoActions.push(...await this.finalizeOperation(undoOperation));
    undoActions.push(...this.finalizeBusbarDetaching());
    // action itself
    undoActions.push(of(undoOperation));
    // post-actions
    undoActions.push(of(await this.finalizeLabelStripCreation()));
    undoActions.push(...this.finalizeBusbarCreation());

    return forkJoin(undoActions).pipe(map((operations) => operations.filter((operation) => !!operation)));
  }

  private addAdditionalUndoActions(operationsToPush: IOperation[], additionalOperations: IOperation[] = []): void {
    if (operationsToPush.length > 0) {
      if (operationsToPush.length === 1 && !additionalOperations.length) {
        this.pushUndoInternal(operationsToPush[0]);
      } else {
        this.pushUndoOperationsAsSequence(operationsToPush, additionalOperations);
      }
    }
  }

  private async finalizeLabelStripCreation(): Promise<IOperation> {
    const containers = await this.replaceLabelingHelper.createPendingLabelingArticles();
    return this.undoRedoUtils.createUndoAttachOperationByArticleContainers(containers);
  }


  private finalizeBusbarDetaching(): Observable<IOperation>[] {
    const additionalOperations: IOperation[] = this.replaceBusbarHelper.detachOperations;
    return additionalOperations.map(o => of(o));
  }

  private finalizeBusbarCreation(): Observable<IOperation>[] {
    return this.replaceBusbarHelper.createPendingBusbarArticles();
  }


  private async finalizeMoveOperation(moveOperation: IOperation): Promise<IOperation> {
    let additionalOperation: IOperation = null;
    if (this.isMoveOperation(moveOperation) && moveOperation.isDistanceBetweenArticlesChanged()) {
      const articleContainersToDetach: ArticleContainer[] = this.labelStripArticleManager.getLabelStripsOnAdaptersToDetachByMovedArticles(
        moveOperation.getMoveArticlesMap(),
        moveOperation.isArticlesOrderChanged(),
        true
      );
      if (articleContainersToDetach.length > 0) {
        additionalOperation = await this.replaceLabelingHelper.detachLabelStrips(articleContainersToDetach);
      }
    }
    return additionalOperation;
  }

  private async finalizeOperation(operation: IOperation): Promise<Observable<IOperation>[]> {
    let preActionOperations: IOperation[] = [];
    const finalizedMoveOperation: IOperation = await this.finalizeMoveOperation(operation);
    if (finalizedMoveOperation) {
      preActionOperations.push(finalizedMoveOperation);
    }
    if (ArrayUtils.isNotEmpty(this.directPrintingHelper.storedOperations)) {
      preActionOperations = this.directPrintingHelper.storedOperations;
      this.directPrintingHelper.clearStoredOperations();
    }
    return preActionOperations.map((o) => of(o));
  }

  private getAdditionalOperations(undoOperation: IOperation): IOperation[] {
    const additionalOperations: IOperation[] = [];
    if (this.isMoveOperation(undoOperation) && undoOperation.isDistanceBetweenArticlesChanged()) {
      const directPrintingsOperation: IOperation = this.directPrintingHelper.recreateDirectPrintingsOnMoveOperation(undoOperation);
      if (directPrintingsOperation) {
        additionalOperations.push(directPrintingsOperation);
      }
    } else if (undoOperation instanceof UndoRotateArticlesOperation) {
      const directPrintingsOperation: IOperation = this.directPrintingHelper.recreateDirectPrintingsOnRotateOperation(
        undoOperation.rotatedArticleUsageIds
      );
      if (directPrintingsOperation) {
        additionalOperations.push(directPrintingsOperation);
      }
    }
    return additionalOperations;
  }

  private pushUndoOperationsAsSequence(operations: IOperation[], additionalOperations: IOperation[] = []): void {
    this.startUndoOperationSequence();
    operations.forEach((operation) => this.pushUndoInternal(operation));
    this.finalizeUndoOperationSequence(additionalOperations);
  }

  public async detachArticlesByUsageIds(articleUsageIdsToDetach: string[], pushUndo: boolean = true): Promise<IOperation> {
    let detachOperation: IOperation = null;
    if (articleUsageIdsToDetach.length > 0) {
      this.selectionModelService.removeAllSelections();

      detachOperation = this.undoRedoUtils.createUndoDetachOperationByUsageIds(articleUsageIdsToDetach);
      if (pushUndo) {
        this.pushUndoInternal(detachOperation);
      }

      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(articleUsageIdsToDetach);
    }
    return detachOperation;
  }

  private pushUndoInternal(operation: IOperation): void {
    this.undoRedoManager.pushUndo(operation);
  }

  public startUndoOperationSequence(): void {
    this.undoRedoManager.startUndoOperationSequence();
  }

  public finalizeUndoOperationSequence(additionalOperations: IOperation[] = []): void {
    this.undoRedoManager.finalizeUndoOperationSequence(additionalOperations);
  }

  public clearAll(): void {
    this.undoRedoManager.clearAll();
  }

  public createUndoDetachOperationByUsageIds(articleUsageIds: string[]): UndoDetachArticlesOperation {
    return this.undoRedoUtils.createUndoDetachOperationByUsageIds(articleUsageIds)
  }

  public createUndoAttachOperationByUsageIds(articleUsageIds: string[]): UndoAttachArticlesOperation {
    return this.undoRedoUtils.createUndoAttachOperationByUsageIds(articleUsageIds);
  }

  private isMoveOperation(operation: IOperation): operation is IMoveArticlesOperation {
    return (operation as IMoveArticlesOperation).getMoveArticlesMap !== undefined;
  }

  // removes a group of articles (without checking for preset/composite usages)and returns the corresponding undoOperation
  // (which is not pushed to the undoManager here, so it can be grouped in a sequence by the function which called this one)
  public async forceRemoveArticlesAndReturnUndoOperations(articleUsageIds: string[]): Promise<UndoDetachArticlesOperation> {
    let detachedUsageIds: string[] = [];
    let resultOperation: UndoDetachArticlesOperation = null;
    for (const usageId of articleUsageIds) {
      detachedUsageIds = detachedUsageIds.concat(await this.detachArticleUsageService.internalDetachArticleUsageFromProjectById(usageId));
    }
    if (ArrayUtils.isNotEmpty(detachedUsageIds)) {
      this.selectionModelService.removeAllSelections();
      resultOperation = this.createUndoDetachOperationByUsageIds(detachedUsageIds);
      this.applicationRefreshService.notifyProjectChange();
    }
    return resultOperation;
  }

  public async doWithSequence<T>(asyncFunc: (pushOperation: (undo: IOperation) => void) => Promise<T>): Promise<T> {
    return this.undoRedoManager.doWithSequence(asyncFunc);
  }

  public popAllFromRunningSequence(): IOperation[] {
    return this.undoRedoManager.popAllFromRunningSequence();
  }

  public pushInsertArticleByLocationUndoOperation(insertedArticleData: InsertedArticleData) {
    const undoAttachArticlesOperation = this.undoRedoUtils.createUndoAttachOperationByUsageIds(insertedArticleData.insertedArticleGUIDs);
    if (undoAttachArticlesOperation) {
      const undoInsertArticlesByLocationOperation = new UndoInsertArticlesByLocationOperation(
        undoAttachArticlesOperation,
        insertedArticleData.moveArticlesMap,
        this.insertArticleUtils,
      );
      undoInsertArticlesByLocationOperation.additionalOperations = insertedArticleData.undoOperations;
      this.undoRedoManager.pushUndo(undoInsertArticlesByLocationOperation);
    }
  }
}
